import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { useTransientEventBadge } from '../../hooks/useTransientEventBadge'
import { Category } from '../../models/Category'
import { Item } from '../../models/Item'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import { ImageView } from '../common/ImageView'
import { Checkbox } from '../common/fields/Checkbox'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { ArrangeableCustomOptions } from '../workflows/ArrangeableCustomOptions'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    category: Category
    onDelete?: () => void
    onClose?: () => void
    onSave: (category: Category, photo?: File) => void
    isLoading?: boolean
    componentItems: Item[]
    enableAllFields?: boolean
}

export function CategoryPage({
    category,
    onDelete,
    onClose,
    onSave,
    isLoading,
    componentItems,
    enableAllFields = true,
}: Props) {
    const owner = PSApplication.owner()

    const { isBadgeShowing: isItemAddedBadgeShowing, onEvent: onItemAdded } =
        useTransientEventBadge()

    const [scratchCategory, setScratchCategory] = useState(category)

    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(
        category.image?.uri ? { type: 'url', url: category.image.uri } : null
    )

    const selectableItems = useMemo(() => {
        const selectedItemIDs = new Set(scratchCategory.listings)

        return componentItems
            .filter((item) => !selectedItemIDs.has(item.id ?? ''))
            .map((item) => {
                return {
                    id: item.id ?? '',
                    title: item.name ?? '',
                    tertiaryView: (
                        <ImageView
                            image={item.image}
                            className="size-9 rounded-full border-2 border-white"
                            fallbackIcon={{
                                icon: 'tag.small',
                                background: 'round',
                                className: 'size-9',
                            }}
                        />
                    ),

                    item,
                }
            })
    }, [componentItems, scratchCategory])

    const selectedItems = useMemo(() => {
        return scratchCategory.listings
            .map((listing) =>
                componentItems.find((item) => item.id === listing || item.editionItem === listing)
            )
            .filter((item) => item !== undefined)
    }, [scratchCategory.listings, componentItems])

    const isNextEnabled = useMemo(() => {
        return scratchCategory.name !== '' && scratchCategory.listings?.length > 0
    }, [scratchCategory.name, scratchCategory.listings])

    return (
        <WorkflowPage
            title={'Category'}
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete category
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() => {
                            onSave(
                                scratchCategory,
                                selectedPhoto?.type === 'file' ? selectedPhoto.file : undefined
                            )
                        }}
                        disabled={isLoading || !isNextEnabled}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-4">
                <WorkflowPage.Card title="General">
                    <WorkflowPage.PhotoPicker
                        onSelectPhoto={(photoFile) => {
                            setSelectedPhoto({ type: 'file', file: photoFile })
                        }}
                        onClearPhoto={() => setSelectedPhoto(null)}
                        selectedPhoto={selectedPhoto}
                    />

                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="grid.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            onClear={() => {
                                setScratchCategory((c) => c?.clone({ name: '' }))
                            }}
                            value={scratchCategory?.name}
                            onChange={(e) => {
                                setScratchCategory((c) => c?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            isAutoResizing
                            size="medium"
                            placeholder="Enter description"
                            value={scratchCategory?.memo}
                            onClear={() => {
                                setScratchCategory((c) => c?.clone({ memo: '' }))
                            }}
                            onChange={(e) => {
                                setScratchCategory((c) => c?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {owner.allowWidget && (
                        <WorkflowPage.IconSection
                            title="Show online"
                            description="Allow clients to purchase this service online"
                            icon={<Icon icon="computerDollar.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchCategory?.isPublic}
                                    onChange={(e) => {
                                        setScratchCategory((c) => c?.clone({ is_public: e }))
                                    }}
                                />
                            }
                        />
                    )}
                </WorkflowPage.Card>

                {enableAllFields && (
                    <WorkflowPage.Card title="Items">
                        <WorkflowPage.IconSection
                            title="Items"
                            icon={<Icon icon="tags.large" background="round" />}
                            secondaryText={isItemAddedBadgeShowing ? 'Added' : undefined}
                            secondaryTextClassName="font-medium text-success-400"
                        >
                            <SearchableSelect
                                items={selectableItems}
                                onSelectItem={(item) => {
                                    setScratchCategory((c) =>
                                        c?.clone({
                                            listings: [...(c?.listings ?? []), item?.id ?? ''],
                                        })
                                    )
                                    onItemAdded()
                                }}
                                titleWeight="bold"
                                placeholder="Type to search"
                                shouldRefocusAfterSelection
                            />
                            <ArrangeableCustomOptions
                                droppableId="edit-category-items"
                                options={selectedItems.map((item, idx) => ({
                                    id: item.id ?? '',
                                    title: item.name ?? '',
                                    seqnum: idx,
                                    onDelete: () => {
                                        setScratchCategory((c) =>
                                            c?.clone({
                                                listings: c?.listings?.filter(
                                                    (id) => id !== item.id
                                                ),
                                            })
                                        )
                                    },
                                    ...item.iconConfig,
                                }))}
                                shouldAlignDeleteButtonWithContainingCard
                                onDragEnd={(_s, _d, list) => {
                                    setScratchCategory((c) =>
                                        c?.clone({ listings: list.map((item) => item.id) })
                                    )
                                }}
                            />
                        </WorkflowPage.IconSection>
                    </WorkflowPage.Card>
                )}
            </div>
        </WorkflowPage>
    )
}
